import type { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { localStorageService } from "../../services/LocalStorageService";
import { DEFAULT_WEEK_START_DAY } from "../../utils/models";
import { useAuth } from "../AuthProvider";
import { useChosenDepartment } from "../ChosenDepartment";
import { WeekIndexContext } from "./context";

export const WeekIndexProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const [weekIndex, setWeekIndex] = useState(0);
	const [shownWeekStart, setShownWeekStart] = useState<DateTime>();

	// Single source of truth for the dynamic week-start day, shared by Staffing, Profile, and Master
	// Schedule and persisted per (department, user) under Master Schedule's existing localStorage key.
	// DAT-651: the fallback is Sunday, NOT `chosenDepartment.week_start_day`. That column anchors the
	// payroll/OT week, and using it here meant editing "Payroll start day" in Department Config shifted
	// the grid of every user who had never picked a day for themselves. Sunday also matches what the
	// grid renders when StaffingDynamicWeekStart is off, so the two flag states agree.

	const [viewStartDay, setViewStartDayState] = useState<number>(
		() => localStorageService.getViewWeekStartDay(chosenDepartment.department_id, userId ?? "") ?? DEFAULT_WEEK_START_DAY,
	);

	// Reload the persisted value only when the department or user changes — tracking userId too so a
	// late-arriving id (token auth resolves before userInfo) still restores the saved choice. Uses the
	// raw setter so reading a value never writes it back.
	const lastLoadedKeyRef = useRef<string>(`${chosenDepartment.department_id}|${userId ?? ""}`);
	useEffect(() => {
		const loadKey = `${chosenDepartment.department_id}|${userId ?? ""}`;
		if (lastLoadedKeyRef.current === loadKey) return;
		lastLoadedKeyRef.current = loadKey;
		setViewStartDayState(localStorageService.getViewWeekStartDay(chosenDepartment.department_id, userId ?? "") ?? DEFAULT_WEEK_START_DAY);
	}, [chosenDepartment.department_id, userId]);

	const setViewStartDay = useCallback(
		(day: number) => {
			setViewStartDayState(day);
			if (userId) localStorageService.setViewWeekStartDay(chosenDepartment.department_id, day, userId);
		},
		[chosenDepartment.department_id, userId],
	);

	return (
		<WeekIndexContext.Provider value={{ weekIndex, setWeekIndex, shownWeekStart, setShownWeekStart, viewStartDay, setViewStartDay }}>
			{children}
		</WeekIndexContext.Provider>
	);
};
